import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import getNextObjectKey from 'services/numbers/getNextObjectKey';
import InputLabel from 'components/layout/forms/InputLabel';
import IProductInstallationCategory from 'api/interfaces/products/IProductInstallationCategory';
import LinkButton from 'components/layout/buttons/LinkButton';
import translations from 'services/translations/translations';
import { moveItemWithinSubset } from 'services/arrays/moveItem';

import { ACCOMMODATION } from 'services/enums/offers/installationTypes';
import { PositionUnitPriceHeader } from '../PositionUnitPrice';
import { InstallationHistoryRows } from '../PositionHistory';

import getDefaultInstallation from './getDefaultInstallation';
import IInstallation from '../../IInstallation';
import Installation from './Installation';
import InstallationsSum from './InstallationsSum';
import RowOrder from '../RowOrder';
import { OTHER_EXPENSE } from '../../../../services/installationTypes';

const OtherExpenses = ({
  locked,
  installations,
  quantity,
  onRemoveInstallation,
  reordering,
  setInstallations,
  targetMarginPercent,
}: {
  // Deleted rows are kept by the calculation window so they can be restored
  onRemoveInstallation: (installation: IInstallation, index: number) => void;
  locked: boolean;
  installations: IInstallation[];
  quantity: number;
  reordering: boolean;
  setInstallations: (installations: IInstallation[]) => void;
  // New rows follow the target margin % of the window
  targetMarginPercent: string;
}): JSX.Element => {
  const { t } = useTranslation([translations.common, translations.offers]);

  const [openedInstallation, setOpenedInstallation] = useState<null | number>(
    null
  );

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );

  const addOtherExpense = () => {
    const key = getNextObjectKey(installations, 'key');
    setInstallations([
      ...installations,
      {
        ...getDefaultInstallation(key, ACCOMMODATION),
        followsTargetMargin: true,
      },
    ]);
  };

  const removeInstallation = (installation: IInstallation) => {
    onRemoveInstallation(
      installation,
      installations.findIndex(
        (existingInstallation) => existingInstallation.key === installation.key
      )
    );
    setInstallations(
      installations.filter(
        (existingInstallation) => existingInstallation.key !== installation.key
      )
    );
  };

  const setInstallation = (installation: IInstallation) => {
    setInstallations(
      installations.map((existingInstallation) =>
        existingInstallation.key === installation.key
          ? installation
          : existingInstallation
      )
    );
  };

  const isOtherExpense = (installation: IInstallation) =>
    installationCategories.some(
      (installationCategory: IProductInstallationCategory) =>
        installationCategory.type === OTHER_EXPENSE &&
        installationCategory.category === installation.category
    );

  const otherExpenses = installations.filter(isOtherExpense);

  // Installations are in the same list; they keep their places
  const moveOtherExpense = (fromIndex: number, toIndex: number) =>
    setInstallations(
      moveItemWithinSubset(installations, isOtherExpense, fromIndex, toIndex)
    );

  const offerIsLocked = () => locked;

  const SMALL_COLUMN_CLASS = 'small-column';

  return (
    <>
      <h2>{t(`${translations.calculationWindow}:Other expenses`)}</h2>
      <RowOrder count={otherExpenses.length} reordering={reordering}>
        <Row className="installation-row">
          <Col>
            <InputLabel>{t(`${translations.common}:Category`)}</InputLabel>
          </Col>
          <Col>
            <InputLabel>
              {t(`${translations.calculationWindow}:Expense`)}
            </InputLabel>
          </Col>
          <Col className="icon-column" />
          <Col>
            <InputLabel>{t(`${translations.common}:Quantity`)}</InputLabel>
          </Col>
          <Col>
            <InputLabel>
              {t(`${translations.calculationWindow}:Expense`)}
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {`${t(`${translations.calculationWindow}:Percentage`)} %`}
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>{t(`${translations.offers}:Expense`)}</InputLabel>
          </Col>
          <Col>
            <InputLabel>{t(`${translations.offers}:Selling price`)}</InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <PositionUnitPriceHeader />
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {`${t(`${translations.offers}:Contribution margin`)} €`}
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {t(`${translations.calculationWindow}:Expense short`)}{' '}
              {t(`${translations.common}:total-short`)}
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {t(`${translations.calculationWindow}:Selling short`)}{' '}
              {t(`${translations.common}:total-short`)}
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {t(`${translations.offers}:Contribution margin`)}{' '}
              {t(`${translations.common}:total-short`)} €
            </InputLabel>
          </Col>
          <Col className={SMALL_COLUMN_CLASS}>
            <InputLabel>
              {`${t(`${translations.offers}:Contribution margin`)} %`}
            </InputLabel>
          </Col>
          <Col className="icon-column" />
        </Row>
      </RowOrder>
      {otherExpenses.map((installation, index) => (
        <React.Fragment key={`other-expense-${installation.key}`}>
          <RowOrder
            count={otherExpenses.length}
            index={index}
            move={moveOtherExpense}
            reordering={reordering}
          >
            <Installation
              locked={locked}
              installation={installation}
              openedInstallation={openedInstallation}
              quantity={quantity}
              removeInstallation={removeInstallation}
              setInstallation={setInstallation}
              setOpenedInstallation={setOpenedInstallation}
              targetMarginPercent={targetMarginPercent}
              type={OTHER_EXPENSE}
            />
          </RowOrder>
          {reordering ? null : (
            <InstallationHistoryRows
              installation={installation}
              installations={installations}
              type={OTHER_EXPENSE}
            />
          )}
        </React.Fragment>
      ))}
      {offerIsLocked() ? null : (
        <LinkButton onClick={addOtherExpense}>
          {`+ ${t(`${translations.common}:Add row`)}`}
        </LinkButton>
      )}
      <InstallationsSum quantity={quantity} installations={otherExpenses} />
    </>
  );
};

export default OtherExpenses;
